import { TRACKING_STATUS } from '@modules/ws/constants/TrackingProcess'
import { formatFeeBillingCpf } from '@modules/ws/constants/FeeBilling'
import { primeiroDefinido } from '@modules/ws/utils/valorDefinido'

// Getters
export const onboardingProcesses = (state) => {
  return state.onboardingProcesses.data || []
}
export const onboardingProcessesLoading = (state) => {
  return state.onboardingProcesses.loading
}
export const onboardingProcessesLastUpdate = (state) => {
  return state.onboardingProcesses.lastUpdate
}

export const accountMigrations = (state) => {
  return state.accountMigrations.data || []
}
export const accountMigrationsLoading = (state) => {
  return state.accountMigrations.loading
}
export const accountMigrationsLastUpdate = (state) => {
  return state.accountMigrations.lastUpdate
}

export const custodyTransfers = (state) => {
  return state.custodyTransfers.data || []
}
export const custodyTransfersLoading = (state) => {
  return state.custodyTransfers.loading
}
export const custodyTransfersLastUpdate = (state) => {
  return state.custodyTransfers.lastUpdate
}

export const averagePriceRequests = (state) => {
  return state.averagePriceRequests.data || []
}
export const averagePriceRequestsLoading = (state) => {
  return state.averagePriceRequests.loading
}
export const averagePriceRequestsLastUpdate = (state) => {
  return state.averagePriceRequests.lastUpdate
}

export const feeBillingBatches = (state) => {
  return state.feeBillingBatches.data || []
}
export const feeBillingBatchesLoading = (state) => {
  return state.feeBillingBatches.loading
}
export const feeBillingBatchesLastUpdate = (state) => {
  return state.feeBillingBatches.lastUpdate
}

export const professionals = (state) => {
  return state.professionals.data || []
}
export const professionalsLoading = (state) => {
  return state.professionals.loading
}
export const professionalsLastUpdate = (state) => {
  return state.professionals.lastUpdate
}

export const clientSwaps = (state) => {
  return state.clientSwaps.data || []
}
export const clientSwapsLoading = (state) => {
  return state.clientSwaps.loading
}
export const clientSwapsLastUpdate = (state) => {
  return state.clientSwaps.lastUpdate
}

// Wave 2a (PLAN-v1.1 seção W2a) — matriz cliente × processo, landing do
// Backoffice. Cruza os 5 slices de tracking manual por cliente (onboarding,
// migração, custódia, preço médio, cobrança — profissionais fica de fora,
// é outra dimensão: profissional da carteira, não cliente do escritório).
// Chave de cruzamento (contrato Wave 1d): `id_cliente_app`, ligado a cada
// item via enrichment em `apiWsBackoffice*._enrichComClientesReais` (o
// caller garante `ws/clients` carregado antes). Processo sem vínculo a um
// cliente real (`id_cliente_app == null`, ex: onboarding via mesa/convite
// ainda sem virar cliente) cai no fallback por nome normalizado — mesmo
// cliente/lead nunca duplica linha na matriz.
const PROCESSO_VAZIO = {
  onboarding: null,
  migracao: null,
  custodia: null,
  preco_medio: null,
  cobranca: null,
}

function normalizeNomeCliente(nome) {
  return String(nome || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
}

/**
 * Label da primeira etapa ainda não concluída de `etapas[]` — null quando o
 * processo não usa etapas (preço médio, cobrança usam `status` direto) ou
 * quando todas já concluíram (nada mais para destacar).
 */
function primeiraEtapaPendenteLabel(etapas) {
  if (!Array.isArray(etapas) || !etapas.length) return null
  const etapa = etapas.find((item) => item && item.status !== TRACKING_STATUS.CONCLUIDO)
  return etapa ? etapa.label : null
}

/**
 * Valor comparável de "quando" um item foi criado, só para decidir qual
 * processo do MESMO tipo é o mais recente para um cliente — nunca comparado
 * entre tipos diferentes. Onboarding/migração não têm `criado_em` (ver
 * `mockOnboarding.js`/`mockMigrations.js`): o `id` (sequencial via
 * `nextMockId`/seedBase) é o proxy de recência nesse caso. Custódia/preço
 * médio têm `criado_em` ISO. Cobrança usa `data_upload` do LOTE (a
 * recência é do lote, não da linha do cliente dentro dele).
 */
function valorRecencia(item) {
  if (!item) return 0
  if (item.criado_em) return item.criado_em
  if (item.data_upload) return item.data_upload
  return Number(item.id) || 0
}

function chaveCliente(idClienteApp, nome) {
  if (idClienteApp === null || idClienteApp === undefined) {
    return `nome:${normalizeNomeCliente(nome)}`
  }
  return `id:${idClienteApp}`
}

/** Garante a linha do cliente no acumulador, criando com processos vazios na 1ª vez que aparece. */
function garantirLinha(linhasPorChave, { chave, idClienteApp, nome, identificadorFallback }) {
  if (!linhasPorChave[chave]) {
    linhasPorChave[chave] = {
      clienteKey: chave,
      id_cliente_app: primeiroDefinido(idClienteApp, null),
      nome: nome || null,
      identificadorFallback: identificadorFallback || null,
      processos: { ...PROCESSO_VAZIO },
    }
  }
  const linha = linhasPorChave[chave]
  if (linha.id_cliente_app == null && idClienteApp != null) linha.id_cliente_app = idClienteApp
  if (!linha.nome && nome) linha.nome = nome
  if (!linha.identificadorFallback && identificadorFallback) linha.identificadorFallback = identificadorFallback
  return linha
}

/**
 * Aplica UM item de processo à matriz: resolve/cria a linha do cliente e,
 * se `itemRecencia` for mais recente que o que já está na célula `tipo`,
 * substitui. A recência bruta usada na comparação fica só em
 * `recenciaPorCelula` — nunca vaza pro shape final da célula
 * (`{status, etapaAtual, id}`).
 */
function aplicarProcesso(linhasPorChave, recenciaPorCelula, tipo, params) {
  const { idClienteApp, nome, id, status, etapas, itemRecencia, identificadorFallback } = params
  if (idClienteApp == null && !nome) return

  const chave = chaveCliente(idClienteApp, nome)
  const linha = garantirLinha(linhasPorChave, { chave, idClienteApp, nome, identificadorFallback })
  const chaveCelula = `${chave}|${tipo}`
  const atual = recenciaPorCelula[chaveCelula]
  if (atual !== undefined && valorRecencia(itemRecencia) <= atual) return

  recenciaPorCelula[chaveCelula] = valorRecencia(itemRecencia)
  linha.processos[tipo] = { status: status || null, etapaAtual: primeiraEtapaPendenteLabel(etapas), id }
}

/** Onboarding/migração/custódia compartilham o shape `TrackingProcess` (`processo.cliente.{nome,id_cliente_app}`). */
function aplicarProcessoPadrao(linhasPorChave, recenciaPorCelula, tipo, lista) {
  ;(lista || []).forEach((processo) => {
    const cliente = (processo && processo.cliente) || {}
    aplicarProcesso(linhasPorChave, recenciaPorCelula, tipo, {
      idClienteApp: primeiroDefinido(cliente.id_cliente_app, null),
      nome: cliente.nome,
      id: processo && processo.id,
      status: processo && processo.status,
      etapas: processo && processo.etapas,
      itemRecencia: processo,
    })
  })
}

/** Preço médio é flat (`solicitacao.id_cliente_app`/`nome_cliente`, sem `etapas`). */
function aplicarPrecoMedio(linhasPorChave, recenciaPorCelula, lista) {
  ;(lista || []).forEach((solicitacao) => {
    if (!solicitacao) return
    aplicarProcesso(linhasPorChave, recenciaPorCelula, 'preco_medio', {
      idClienteApp: primeiroDefinido(solicitacao.id_cliente_app, null),
      nome: solicitacao.nome_cliente,
      id: solicitacao.id,
      status: solicitacao.status,
      etapas: null,
      itemRecencia: solicitacao,
    })
  })
}

/**
 * Cobrança agrega VÁRIOS clientes por lote (`lote.clientes[i]`) — sem
 * `id_cliente_app` a linha não tem como ser atribuída a um cliente (o
 * template CSV não traz nome, só CPF) e é ignorada na matriz. A recência
 * comparada é a do LOTE (`data_upload`), não da linha — "célula cobrança
 * deriva da linha do cliente no lote mais recente" (contrato da wave).
 */
function aplicarCobranca(linhasPorChave, recenciaPorCelula, lotes) {
  ;(lotes || []).forEach((lote) => {
    const linhasClientes = (lote && lote.clientes) || []
    linhasClientes.forEach((clienteLinha) => {
      if (!clienteLinha || clienteLinha.id_cliente_app == null) return
      aplicarProcesso(linhasPorChave, recenciaPorCelula, 'cobranca', {
        idClienteApp: clienteLinha.id_cliente_app,
        nome: null,
        id: lote.id,
        status: clienteLinha.status,
        etapas: null,
        itemRecencia: lote,
        identificadorFallback: formatFeeBillingCpf(clienteLinha.cpf),
      })
    })
  })
}

/**
 * Matriz cliente × processo do Backoffice (Wave 2a) — uma linha por cliente
 * (real ou lead ainda sem `id_cliente_app`), colunas = os 5 processos de
 * tracking manual. Ver regras de chave/recência nos helpers acima.
 */
export const backofficeClientMatrix = (state) => {
  const linhasPorChave = {}
  const recenciaPorCelula = {}

  aplicarProcessoPadrao(linhasPorChave, recenciaPorCelula, 'onboarding', state.onboardingProcesses.data)
  aplicarProcessoPadrao(linhasPorChave, recenciaPorCelula, 'migracao', state.accountMigrations.data)
  aplicarProcessoPadrao(linhasPorChave, recenciaPorCelula, 'custodia', state.custodyTransfers.data)
  aplicarPrecoMedio(linhasPorChave, recenciaPorCelula, state.averagePriceRequests.data)
  aplicarCobranca(linhasPorChave, recenciaPorCelula, state.feeBillingBatches.data)

  return Object.values(linhasPorChave)
    .map(({ identificadorFallback, ...linha }) => ({
      ...linha,
      nome: linha.nome || (identificadorFallback ? `Cliente ${identificadorFallback}` : 'Cliente sem nome'),
    }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' }))
}

